/**
 * The crop and rotate editor a fan sees right after picking a banner or a
 * photo, before anything uploads (ISSUE-1094).
 *
 * - EXIF: the browser turns a phone photo upright for both the frame and the
 *   export, because both read the same decoded picture (see imageCropCanvas).
 * - Rotation moves only in quarter turns, from the two buttons. A two finger
 *   twist does not rotate (no `onRotationChange`).
 * - The stage waits for the dialog's opening animation before it mounts the
 *   cropper, which measures its box once on mount.
 *
 * Cancel uploads nothing: the editor never touches the network. It hands the
 * framed file to `onConfirm` and the caller uploads it exactly as it would
 * have uploaded the picked file.
 */

import { type ReactNode, useEffect, useId, useRef, useState } from "react";
import Cropper, { type Area } from "react-easy-crop";

import { Button } from "../Button/Button";
import { Dialog } from "../Dialog/Dialog";
import { InlineBanner } from "../InlineBanner/InlineBanner";
import { Spinner } from "../Spinner/Spinner";
import {
  decodeImageFile,
  renderCroppedFile,
  type typeDecodedImage,
} from "./imageCropCanvas";
import {
  IMAGE_CROP_KEYBOARD_STEP_PX,
  IMAGE_CROP_MAX_ZOOM,
  IMAGE_CROP_MIN_ZOOM,
  IMAGE_CROP_ZOOM_STEP,
  resolveCropArea,
  rotateBy,
  type typeCropRotation,
  type typeReportedCropArea,
} from "./imageCropMath";

export const IMAGE_CROP_HINT =
  "Drag the picture to frame it. Pinch or use the slider to zoom.";
export const IMAGE_CROP_ZOOM_LABEL = "Zoom";
export const IMAGE_CROP_ROTATE_LEFT_LABEL = "Rotate left";
export const IMAGE_CROP_ROTATE_RIGHT_LABEL = "Rotate right";
export const IMAGE_CROP_CANCEL_LABEL = "Cancel";
export const IMAGE_CROP_CONFIRM_LABEL = "Use photo";
export const IMAGE_CROP_RENDERING_LABEL = "Getting your photo ready";
export const IMAGE_CROP_LOADING_LABEL = "Opening your picture";
export const IMAGE_CROP_FRAME_LABEL =
  "Picture framing. Use the arrow keys to move the picture.";
export const IMAGE_CROP_DECODE_FAILED_MESSAGE =
  "We can't open that picture. Try a JPEG or PNG instead.";
export const IMAGE_CROP_RENDER_FAILED_MESSAGE =
  "We couldn't save that framing. Try again.";

export type typeImageCropShape = "rect" | "round";

export type typeImageCropEditorProps = {
  /** The picked file. null keeps the editor closed. */
  file: File | null;
  title: string;
  /** Frame width over height: 3 for a pool banner, 1 for a photo. */
  aspect: number;
  /** "round" draws the frame as a circle; the output is still the square. */
  shape: typeImageCropShape;
  /** Longest edge of the produced image, in pixels. */
  maxOutputEdgePx: number;
  /** Called with the framed, rotated image. The caller closes the editor by setting file to null. */
  onConfirm: (cropped: File) => void;
  /** Called on Cancel, the X and Escape. Nothing was produced or uploaded. */
  onCancel: () => void;
  testId: string;
};

const RotateLeftIcon = () => (
  <svg
    className="h-icon-md w-icon-md"
    viewBox="0 0 20 20"
    fill="none"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="M7 4 4 7l3 3M4 7h7a5 5 0 1 1 0 10H8"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const RotateRightIcon = () => (
  <svg
    className="h-icon-md w-icon-md"
    viewBox="0 0 20 20"
    fill="none"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="m13 4 3 3-3 3m3-3H9a5 5 0 1 0 0 10h3"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

type typeCropStageProps = {
  imageUrl: string;
  crop: { x: number; y: number };
  zoom: number;
  rotation: typeCropRotation;
  aspect: number;
  shape: typeImageCropShape;
  onCropChange: (crop: { x: number; y: number }) => void;
  onZoomChange: (zoom: number) => void;
  onAreaReported: (area: Area) => void;
};

const CropStage = (props: typeCropStageProps): ReactNode => {
  const stageRef = useRef<HTMLDivElement>(null);
  const [isStageReady, setIsStageReady] = useState(false);

  useEffect(() => {
    // The dialog opens with `sm-dialog-in`, a scale from 0.97. The cropper
    // measures its box once on mount, so mounting mid-animation would draw a
    // frame about 3% smaller than the area it exports (ISSUE-1094 plan
    // Decision 9). Under reduced motion there is no animation to wait for,
    // and an engine without `getAnimations` (happy-dom, older Safari) mounts
    // at once.
    const dialog = stageRef.current?.closest('[role="dialog"]');
    const animations =
      dialog !== null && dialog !== undefined && "getAnimations" in dialog
        ? dialog.getAnimations()
        : [];
    let isActive = true;
    void Promise.allSettled(
      animations.map((animation) => animation.finished),
    ).then(() => {
      if (isActive) setIsStageReady(true);
    });
    return () => {
      isActive = false;
    };
  }, []);

  return (
    <div ref={stageRef} className="absolute inset-0">
      {isStageReady ? (
        <Cropper
          image={props.imageUrl}
          crop={props.crop}
          zoom={props.zoom}
          rotation={props.rotation}
          aspect={props.aspect}
          cropShape={props.shape}
          showGrid={props.shape === "rect"}
          minZoom={IMAGE_CROP_MIN_ZOOM}
          maxZoom={IMAGE_CROP_MAX_ZOOM}
          keyboardStep={IMAGE_CROP_KEYBOARD_STEP_PX}
          objectFit="contain"
          onCropChange={props.onCropChange}
          onZoomChange={props.onZoomChange}
          onCropComplete={(_area, pixels) => {
            props.onAreaReported(pixels);
          }}
          cropperProps={{ role: "group", "aria-label": IMAGE_CROP_FRAME_LABEL }}
        />
      ) : null}
    </div>
  );
};

type typeEditorStatus =
  "idle" | "loading" | "ready" | "decodeFailed" | "renderFailed";

export const ImageCropEditor = (props: typeImageCropEditorProps): ReactNode => {
  const testId = props.testId;
  const [status, setStatus] = useState<typeEditorStatus>("idle");
  const [image, setImage] = useState<typeDecodedImage | null>(null);
  const [rotation, setRotation] = useState<typeCropRotation>(0);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [reported, setReported] = useState<typeReportedCropArea | null>(null);
  const [isRendering, setIsRendering] = useState(false);
  const zoomInputId = useId();
  // Bumped whenever a pending export stops counting: Cancel, the X, Escape,
  // a new file, and unmount. An export that finishes under an older token is
  // dropped, so a Cancel pressed during "Getting your photo ready" uploads
  // nothing (ISSUE-1094 D5).
  const renderTokenRef = useRef(0);

  useEffect(() => {
    renderTokenRef.current += 1;
    setRotation(0);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setReported(null);
    setIsRendering(false);
    setImage(null);
    if (props.file === null) {
      setStatus("idle");
      return;
    }
    setStatus("loading");
    let isActive = true;
    let decoded: typeDecodedImage | null = null;
    decodeImageFile(props.file).then(
      (result) => {
        decoded = result;
        if (!isActive) {
          URL.revokeObjectURL(result.objectUrl);
          return;
        }
        setImage(result);
        setStatus("ready");
      },
      () => {
        if (isActive) setStatus("decodeFailed");
      },
    );
    return () => {
      isActive = false;
      renderTokenRef.current += 1;
      if (decoded !== null) URL.revokeObjectURL(decoded.objectUrl);
    };
  }, [props.file]);

  const handleCancel = () => {
    renderTokenRef.current += 1;
    setIsRendering(false);
    props.onCancel();
  };

  const handleRotate = (delta: 90 | -90) => {
    setRotation((current) => rotateBy(current, delta));
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  };

  const handleConfirm = async () => {
    if (image === null) return;
    renderTokenRef.current += 1;
    const token = renderTokenRef.current;
    setStatus("ready");
    setIsRendering(true);
    const area = resolveCropArea({
      reported,
      rotation,
      sourceWidth: image.width,
      sourceHeight: image.height,
      aspect: props.aspect,
    });
    try {
      const cropped = await renderCroppedFile({
        image: image.element,
        sourceWidth: image.width,
        sourceHeight: image.height,
        area,
        rotation,
        maxEdgePx: props.maxOutputEdgePx,
        sourceType: image.sourceType,
        sourceName: image.sourceName,
      });
      if (token !== renderTokenRef.current) return;
      setIsRendering(false);
      props.onConfirm(cropped);
    } catch {
      if (token !== renderTokenRef.current) return;
      setIsRendering(false);
      setStatus("renderFailed");
    }
  };

  const isDisabled = image === null;

  return (
    <Dialog
      open={props.file !== null}
      onOpenChange={(next) => {
        if (!next) handleCancel();
      }}
      title={props.title}
      description={IMAGE_CROP_HINT}
      footer={
        <>
          <Button
            variant="secondary"
            onClick={handleCancel}
            data-testid={`${testId}-cancel`}
          >
            {IMAGE_CROP_CANCEL_LABEL}
          </Button>
          <Button
            variant="primary"
            disabled={isDisabled}
            loading={isRendering}
            loadingLabel={IMAGE_CROP_RENDERING_LABEL}
            onClick={() => {
              void handleConfirm();
            }}
            data-testid={`${testId}-confirm`}
          >
            {IMAGE_CROP_CONFIRM_LABEL}
          </Button>
        </>
      }
    >
      <div
        className="flex flex-col gap-4 pb-4"
        data-testid={testId}
        data-crop-shape={props.shape}
        data-crop-aspect={String(props.aspect)}
        data-crop-rotation={String(rotation)}
      >
        {status === "decodeFailed" ? (
          <div data-testid={`${testId}-error`}>
            <InlineBanner
              variant="warning"
              message={IMAGE_CROP_DECODE_FAILED_MESSAGE}
            />
          </div>
        ) : (
          <>
            {status === "renderFailed" ? (
              <div data-testid={`${testId}-error`}>
                <InlineBanner
                  variant="warning"
                  message={IMAGE_CROP_RENDER_FAILED_MESSAGE}
                />
              </div>
            ) : null}
            <div
              className="relative h-64 w-full overflow-hidden rounded-md bg-surface-sunken md:h-80"
              data-testid={`${testId}-stage`}
            >
              {image !== null ? (
                <CropStage
                  imageUrl={image.objectUrl}
                  crop={crop}
                  zoom={zoom}
                  rotation={rotation}
                  aspect={props.aspect}
                  shape={props.shape}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onAreaReported={(area) => {
                    setReported({ rotation, area });
                  }}
                />
              ) : (
                <div
                  className="flex h-full w-full items-center justify-center"
                  data-testid={`${testId}-loading`}
                >
                  <Spinner className="h-icon-lg w-icon-lg text-ink-secondary" />
                  <span className="sr-only">{IMAGE_CROP_LOADING_LABEL}</span>
                </div>
              )}
            </div>
            <div className="flex flex-col gap-2">
              <label
                htmlFor={zoomInputId}
                className="text-caption text-ink-secondary"
              >
                {IMAGE_CROP_ZOOM_LABEL}
              </label>
              <input
                id={zoomInputId}
                type="range"
                min={IMAGE_CROP_MIN_ZOOM}
                max={IMAGE_CROP_MAX_ZOOM}
                step={IMAGE_CROP_ZOOM_STEP}
                value={zoom}
                disabled={isDisabled}
                onChange={(event) => {
                  setZoom(Number(event.target.value));
                }}
                className="h-touch-target w-full accent-primary-solid"
                data-testid={`${testId}-zoom`}
              />
            </div>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                className="flex-1"
                disabled={isDisabled}
                iconLeading={<RotateLeftIcon />}
                onClick={() => {
                  handleRotate(-90);
                }}
                data-testid={`${testId}-rotate-left`}
              >
                {IMAGE_CROP_ROTATE_LEFT_LABEL}
              </Button>
              <Button
                variant="secondary"
                className="flex-1"
                disabled={isDisabled}
                iconLeading={<RotateRightIcon />}
                onClick={() => {
                  handleRotate(90);
                }}
                data-testid={`${testId}-rotate-right`}
              >
                {IMAGE_CROP_ROTATE_RIGHT_LABEL}
              </Button>
            </div>
          </>
        )}
      </div>
    </Dialog>
  );
};
